import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const NotificacoesContext = createContext();

export const useNotificacoes = () => {
  const context = useContext(NotificacoesContext);
  if (!context) {
    throw new Error('useNotificacoes must be used within NotificacoesProvider');
  }
  return context;
};

export const NotificacoesProvider = ({ children, onConviteNovo }) => {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const [connected, setConnected] = useState(false);

  const connectWebSocket = () => {
    // Only connect if user is logged in
    if (!user || !user.id) {
      return;
    }

    // Get token from localStorage
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
    }

    try {
      // Determine WebSocket URL (ws:// or wss://)
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const wsUrl = `${protocol}//${backendUrl.replace(/^https?:\/\//, '')}/api/ws/notificacoes/${user.id}?token=${token}`;

      console.log('[WS] Conectando WebSocket:', wsUrl);
      console.log('[WS] User ID usado na conexão:', user.id);
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('[WS] ✅ CONECTADO com sucesso!');
        setConnected(true);
      };

      ws.onmessage = (event) => {
        console.log('[WS] 📨 Mensagem recebida (raw):', event.data);
        try {
          const data = JSON.parse(event.data);
          console.log('[WS] 📨 Mensagem parseada:', data);
          handleNotification(data);
        } catch (error) {
          console.error('[WS] ❌ Erro parseando mensagem:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnected(false);
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setConnected(false);

        // Attempt to reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('Attempting to reconnect WebSocket...');
          connectWebSocket();
        }, 5000);
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('Error creating WebSocket:', error);
    }
  };

  const handleNotification = (data) => {
    console.log('[WS] 🔔 Processando notificação tipo:', data.type);

    switch (data.type) {
      case 'convite_novo':
        console.log('[WS] 🎯 CONVITE_NOVO recebido, disparando callback');
        // Trigger callback to show modal
        if (onConviteNovo) {
          onConviteNovo(data);
        } else {
          console.warn('[WS] ⚠️ Callback onConviteNovo não definido!');
        }
        break;

      // [EXAME ENVIO] Handle exame_enviado event
      case 'exame_enviado':
        console.log('[WS] 🧪 EXAME_ENVIADO recebido:', data);
        // Dispatch custom event for SessaoPage to catch
        window.dispatchEvent(new CustomEvent('exame_enviado_ws', { detail: data }));
        break;

      // [TIMER] Handle timer_update event
      case 'timer_update':
        console.log('[WS] ⏱️ TIMER_UPDATE recebido:', data);
        // Dispatch custom event for SharedTimer to catch
        window.dispatchEvent(new CustomEvent('timer_update_ws', { detail: data }));
        break;

      case 'convite_resposta':
        console.log('[WS] 📬 CONVITE_RESPOSTA recebido, status:', data.status);
        // Show toast to remetente
        if (data.status === 'recusada') {
          toast.error(`Dr(a) ${data.convidadoNome} recusou a sua solicitação.`);
        } else if (data.status === 'em_andamento') {
          toast.success(`Dr(a) ${data.convidadoNome} aceitou o convite e entrou na estação.`);
        }
        break;

      default:
        console.log('[WS] ❓ Tipo desconhecido:', data.type);
    }
  };

  useEffect(() => {
    connectWebSocket();

    // Cleanup on unmount
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [user]);

  return (
    <NotificacoesContext.Provider value={{ connected }}>
      {children}
    </NotificacoesContext.Provider>
  );
};
